// SEQ-193 render-test gap. Pure "what text and colour lands in each cell"
// step for PnlMonthOnMonthTable.tsx, kept in its own .ts module (no JSX, no
// CSS import) so a deno test can import it directly: the component file
// itself pulls in PnlWidgetGenericCharts.tsx, which imports a .css file deno
// test cannot resolve. `PnlMonthOnMonthTable.tsx`'s `renderTable` is a thin
// JSX wrapper over `buildMonthOnMonthRenderModel`: any change to what a cell
// shows belongs here, not duplicated in the JSX, so the model a test checks
// is the same one the widget paints from.

import { formatMetricValue, roundsToZeroAtDisplayPrecision } from "../../lib/pnl/formatMetricValue";
import { isSafeModeActive } from "../../lib/calculations";
import type { MonthOnMonthTable } from "./monthOnMonth";
import { DELTA_COLOURS, type DeltaSentiment } from "./widgetPresentation";

/** Shown in place of a project's figures when its fetch failed. Plain words, no figure, so it is safe under Safe Mode. */
export const LOAD_FAILED_TEXT = "Couldn't load";

/** Appended to the header of a month that is not complete yet. */
export const PARTIAL_MONTH_SUFFIX = " (to date)";

export interface MonthOnMonthRenderCell {
  text: string;
}

export interface MonthOnMonthRenderChange {
  text: string;
  /** null means "use the surrounding text colour" (the N/A case: no styling applied). */
  colour: string | null;
}

export interface MonthOnMonthRenderRow {
  key: string;
  label: string;
  cells: MonthOnMonthRenderCell[];
  /** True when this row's data failed to load: it has no cells, and the table draws one "Couldn't load" cell across the row. */
  loadFailed?: boolean;
  /** undefined when the table has no Change column at all; present (possibly N/A) when it does. */
  change?: MonthOnMonthRenderChange;
}

export interface MonthOnMonthRenderModel {
  columnLabels: string[];
  headingLabel: string;
  hasChangeColumn: boolean;
  /** The Change column header, naming the months compared ("Change, Jul to Aug"). */
  changeHeading: string;
  rows: MonthOnMonthRenderRow[];
}

export function buildMonthOnMonthRenderModel(
  table: MonthOnMonthTable,
  headingLabel: string,
  showChange: boolean,
  sentimentForValue?: (deltaValue: number) => DeltaSentiment,
  sentimentForRow?: (rowKey: string, deltaValue: number) => DeltaSentiment,
): MonthOnMonthRenderModel {
  const rows: MonthOnMonthRenderRow[] = table.rows.map((row) => {
    if (row.loadFailed) return { key: row.key, label: row.label, cells: [], loadFailed: true };

    const cells = row.values.map((value) => ({ text: formatMetricValue(value, row.unit) }));

    if (!showChange) return { key: row.key, label: row.label, cells };

    if (!row.change) {
      return { key: row.key, label: row.label, cells, change: { text: "N/A", colour: null } };
    }

    const deltaValue = row.change.deltaValue;
    // A delta that rounds to zero at the unit's display precision is "no
    // change": no sign, neutral colour, no percent.
    const isZero = deltaValue !== null && roundsToZeroAtDisplayPrecision(deltaValue, row.unit);
    const sentiment: DeltaSentiment = isZero
      ? "neutral"
      : sentimentForRow
        ? sentimentForRow(row.key, row.change.deltaValue ?? 0)
        : (sentimentForValue?.(row.change.deltaValue ?? 0) ?? "neutral");
    // A percent or ratio row already is a percentage, so a percent of it
    // ("+2.00% (+16.0%)") reads as two competing numbers: point delta only.
    const isRelativeUnit = row.unit === "percent" || row.unit === "ratio";
    const deltaPercent = isZero || isRelativeUnit ? null : row.change.deltaPercent;
    // Security fix (found while adding this table's first render test): a
    // masked dollar value ("•••••") still let the change-percent through as
    // plain text, e.g. "+••••• (+250.0%)". That percent is derived from the
    // same confidential figures Safe Mode hides, so a currency/hourly-rate
    // row drops it under Safe Mode rather than leaking the rate of change.
    // Non-money units (percent, hours, count, ratio) are never masked at
    // all, so their change-percent stays exactly as before.
    const isMoneyUnit = row.unit === "currency" || row.unit === "currency_per_hour";
    const showPercent = deltaPercent !== null && !(isMoneyUnit && isSafeModeActive());
    const text =
      deltaValue === null
        ? "N/A"
        : isZero
          ? formatMetricValue(0, row.unit)
          : `${deltaValue > 0 ? "+" : ""}${formatMetricValue(deltaValue, row.unit)}${
            showPercent ? ` (${deltaPercent! > 0 ? "+" : ""}${deltaPercent!.toFixed(1)}%)` : ""
          }`;
    return {
      key: row.key,
      label: row.label,
      cells,
      change: { text, colour: deltaValue === null ? null : DELTA_COLOURS[sentiment] },
    };
  });

  return {
    columnLabels: table.columns.map((c) => (c.partial ? `${c.label}${PARTIAL_MONTH_SUFFIX}` : c.label)),
    headingLabel,
    hasChangeColumn: showChange,
    changeHeading: table.changeLabel,
    rows,
  };
}
